import { ApplicationService, EMIService } from "@/service/";
import type {
   TApplication,
   TApplicationQueryParams,
   TApplicationStatus,
   TApplicationWithEmis,
   TCreateApplication,
   TEmi,
   TOverdueApplication,
   TReverseEmi,
   TUser,
} from "@/types";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

type TApplicationStore = {
   workspaceApplicationMap: {
      [slug: string]: { [applicationId: number]: TApplicationWithEmis };
   };
   reversedEmisMap: { [slug: string]: { [emiId: number]: TReverseEmi } };
   search: string;
   setSearch: (query: string) => void;

   // sent to the API, undefined means every status is listed
   status: TApplicationStatus | undefined;
   setStatus: (status: TApplicationStatus | undefined) => void;

   createApplication: (
      slug: string,
      payload: TCreateApplication,
   ) => Promise<TApplicationWithEmis>;
   fetchApplications: (
      slug: string,
      query?: TApplicationQueryParams,
   ) => Promise<TApplication[]>;
   fetchApplication: (
      slug: string,
      id: number | string,
   ) => Promise<TApplicationWithEmis>;
   updateApplication: (
      slug: string,
      id: number | string,
      data:
         | { status: Exclude<TApplication["status"], "pending" | "approved"> }
         | { status: "approved"; date: Date },
   ) => Promise<void>;
   payEMI: (
      slug: string,
      applicationId: number,
      emiId: number,
      fine: number,
      user: TUser,
   ) => Promise<void>;
   undoEMI: (
      slug: string,
      applicationId: number,
      emiId: number,
   ) => Promise<TReverseEmi>;

   // overdue
   fetchOverdueApplications: (slug: string) => Promise<TOverdueApplication[]>;
};

const service = new ApplicationService();
const emiService = new EMIService();

export const useApplicationStore = create<TApplicationStore>()(
   immer((set, get) => ({
      search: "",
      workspaceApplicationMap: {},
      reversedEmisMap: {},
      status: undefined,

      setSearch: (query) => {
         set((store) => {
            store.search = query;
         });
      },

      setStatus: (status) => {
         set((store) => {
            store.status = status;
         });
      },

      createApplication: async (slug, payload) => {
         const response = await service.createApplication(slug, payload);
         set((store) => {
            if (!store.workspaceApplicationMap[slug]) {
               store.workspaceApplicationMap[slug] = {};
            }
            store.workspaceApplicationMap[slug][response.id] = response;
         });
         return response;
      },

      fetchApplications: async (slug, query) => {
         const response = await service.getApplications(slug, query);
         set((store) => {
            if (!store.workspaceApplicationMap[slug]) {
               store.workspaceApplicationMap[slug] = {};
            }
            response.forEach((application) => {
               store.workspaceApplicationMap[slug][application.id] =
                  application;
            });
         });
         return response;
      },

      fetchApplication: async (slug, id) => {
         return service.getApplication(slug, id).then((response) => {
            set((store) => {
               if (response.status === "approved" && response.reverseEmis) {
                  for (const reverseEmi of response.reverseEmis) {
                     if (!store.reversedEmisMap[reverseEmi.emiId]) {
                        store.reversedEmisMap[reverseEmi.emiId] = {};
                     }
                     store.reversedEmisMap[reverseEmi.emiId][reverseEmi.id] =
                        reverseEmi;
                  }
               }
               if (!store.workspaceApplicationMap[slug]) {
                  store.workspaceApplicationMap[slug] = {};
               }
               store.workspaceApplicationMap[slug][Number(id)] = response;
            }, true);
            return response;
         });
      },

      updateApplication: async (slug, id, data) => {
         const existing = get().workspaceApplicationMap?.[slug]?.[Number(id)];
         set((store) => {
            if (existing) {
               store.workspaceApplicationMap[slug][Number(id)] = {
                  ...existing,
                  status: data.status,
               } as TApplicationWithEmis;
            }
         });
         return service
            .updateApplication(slug, id, data)
            .then(() => {
               get().fetchApplication(slug, id);
            })
            .catch((error) => {
               set((store) => {
                  if (existing) {
                     store.workspaceApplicationMap[slug][Number(id)] = {
                        ...existing,
                     } as TApplicationWithEmis;
                  }
               });
               throw error;
            });
      },

      payEMI: async (
         slug: string,
         applicationId: number,
         emiId: number,
         fine: number,
         user: TUser,
      ) => {
         const updateEMIState = (updater: (emi: TEmi) => void) => {
            set((store) => {
               const application =
                  store.workspaceApplicationMap[slug]?.[applicationId];
               if (
                  !application ||
                  application.status !== "approved" ||
                  !application.emis
               )
                  return;

               const emi = application.emis.find((e) => e.id === emiId);
               if (!emi) return;

               updater(emi);
            });
         };

         // Optimistically update EMI
         updateEMIState((emi) => {
            emi.fine = fine;
            emi.collectedBy = user.id;
            emi.transactionId = -1;
         });

         try {
            await emiService.payEMI(slug, emiId, fine).then((response) => {
               updateEMIState((emi) => {
                  emi.transactionId = response.transactionId;
               });
            });
         } catch (err) {
            // Revert on error
            updateEMIState((emi) => {
               emi.fine = 0;
               emi.collectedBy = null;
            });
            throw err;
         }
      },

      undoEMI: async (slug: string, applicationId: number, emiId: number) => {
         return emiService.undoEMI(slug, emiId).then((response) => {
            set((store) => {
               const _emi = store.workspaceApplicationMap[slug]?.[
                  applicationId
               ]?.emis.find(({ id }) => id === emiId);
               if (_emi) {
                  store.workspaceApplicationMap[slug][applicationId].emis =
                     store.workspaceApplicationMap[slug][
                        applicationId
                     ].emis.map((e) => {
                        if (e.id === emiId) {
                           return {
                              ...e,
                              collectedBy: null,
                              fine: 0,
                              transactionId: null,
                           };
                        }
                        return e;
                     });
               }
               if (!store.reversedEmisMap[response.emiId]) {
                  store.reversedEmisMap[response.emiId] = {};
               }
               store.reversedEmisMap[response.emiId][response.id] = response;
            });
            return response;
         });
      },

      fetchOverdueApplications: async (slug) => {
         return service.getOverdueApplications(slug);
      },
   })),
);
